import JoinCTA from '@/components/shared/JoinCTA';
import { Section, SectionParagraph, SectionTitle } from '@/components/shared/Content';
import { Flex, font } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import { useAnalyticsContext } from '@lemonade-hq/nuiza';
import type React from 'react';
import styled from 'styled-components';

const StyledSectionTitle = styled(SectionTitle)`
  font-size: 2.8rem;
  ${MEDIA_QUERIES.tabletAndDesktop`
    font-size: 4rem;
  `}
`;

const StyledSectionParagraph = styled(SectionParagraph)`
  font-size: 2rem;
  ${MEDIA_QUERIES.tabletAndDesktop`
    font-size: 1.8rem;
  `}
`;

const CardTitle = styled.h3`
  ${font('main', { fontWeight: 'bold', fontSize: '1.8rem' })};
  margin-top: 0px;
  margin-bottom: 8px;
  ${MEDIA_QUERIES.tabletAndDesktop`
    ${font('main', { fontWeight: 'bold', fontSize: '2.4rem' })};
    margin-top: 24px;
    margin-bottom: 12px;
  `}
`;

const CardImageWrapper = styled.div<{ $mobileImage: string; $desktopImage: string }>`
  height: 100px;
  width: 100px;
  margin: 0 20px 0 auto;
  background-image: url(${({ $mobileImage }) => $mobileImage});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  flex-shrink: 0;

  ${MEDIA_QUERIES.tabletAndDesktop`
    height: 175px;
    width: 175px;
    margin: 0 auto;
    background-image: url(${({ $desktopImage }: { $desktopImage: string }) => $desktopImage});
  `}
`;

const CardContent = styled(SectionParagraph)`
  text-wrap: balance;
  text-align: left;
  font-size: 1.4rem;
  line-height: 19px;
  ${MEDIA_QUERIES.tabletAndDesktop`
    font-size: 1.6rem;
    text-align: center;
    line-height: 145%;
  `}
`;

const CardWrapper = styled(Flex)`
  text-align: left;
  ${MEDIA_QUERIES.tabletAndDesktop`
    max-width: 280px;
    text-align: center;
  `}
`;

const CardsContainer = styled(Flex)`
  gap: 40px;
  margin-top: 50px;
  ${MEDIA_QUERIES.tabletAndDesktop`
    gap: 70px;
    margin-top: 80px;
  `}
`;

interface CardProps {
  title: string;
  content: string;
  image: StaticImageData;
  imageMobile: StaticImageData;
}

const Card: React.FC<CardProps> = ({ title, content, image, imageMobile }) => {
  return (
    <CardWrapper flexDirection={['row', 'column']} justifyContent="center" alignItems="center">
      <CardImageWrapper $mobileImage={imageMobile.src} $desktopImage={image.src} />
      <div>
        <CardTitle>{title}</CardTitle>
        <CardContent>{content}</CardContent>
      </div>
    </CardWrapper>
  );
};

export interface FeaturesSectionProps {
  title: string;
  subtitle: string;
  cards: CardProps[];
  ctaText: string;
  ctaUrl: string;
  noPaddingTop?: boolean;
}

export const FeaturesSection: React.FC<FeaturesSectionProps> = ({
  title,
  subtitle,
  cards,
  ctaText,
  ctaUrl,
  noPaddingTop = false,
}) => {
  const { trackEvent } = useAnalyticsContext();

  const handleCtaClick = () => {
    trackEvent('vet_landing_page_button_click');
  };

  return (
    <Section darkBackground pt={noPaddingTop ? [60, 0] : [60, 100]} pb={[60, 100]} px="20px">
      <Flex flexDirection="column" gap="20px">
        <StyledSectionTitle>{title}</StyledSectionTitle>
        <StyledSectionParagraph>{subtitle}</StyledSectionParagraph>
      </Flex>

      <CardsContainer alignItems="start" flexDirection={['column', 'row']}>
        {cards.map(card => (
          <Card {...card} key={card.title} />
        ))}
      </CardsContainer>

      <Flex mt={[40, 60]}>
        <JoinCTA absoluteUrl={ctaUrl} onClick={handleCtaClick} flavor="primary" minWidth={160} glowing>
          {ctaText}
        </JoinCTA>
      </Flex>
    </Section>
  );
};
